/**
 * StripeExpressElements is used for PayPal, Apple Pay and Google Pay
 * Pass in Donate.js object
 */

const AnalyticsEvent = require('Scripts/common/analytics-event').default;

const LOW_VALUE_THRESHOLD = 5;
const LOW_VALUE_PAYMENT_METHODS = ['applePay', 'googlePay'];
const ALL_PAYMENT_METHODS = [...LOW_VALUE_PAYMENT_METHODS, 'paypal'];

export default class StripeExpressElements {
	constructor(parent, opts) {
		console.log("StripeExpressElements.constructor");

		this.parent = parent;
		this.opts = opts || {};
		this.container = $('.js-express-checkout');
		this.mounted = false;
		this.mountedPaymentMethods = [];
		this.isSelectedMethod = false;

		this.createElements();
		this.createCheckout(ALL_PAYMENT_METHODS);
	}

	createElements() {
		console.log("StripeExpressElements.createElements");

		const appearance = { 
			variables: {
				borderRadius: '0.375rem', // same as .btn-lg
			},
		}

		this.expressElements = this.parent.stripe.elements({
			mode: 'payment',
			amount: 100, // 100 = £1.00
			currency: 'gbp',
			appearance,
			paymentMethodTypes: Object.keys(this.container[0].dataset),
		});
	}

	createCheckout(paymentMethods) {
		console.log("StripeExpressElements.createCheckout", paymentMethods);

		const options = {
			buttonHeight: 48, // same as .btn-lg
			buttonType: {
				applePay: this.opts.appleBtnText || 'donate',
				googlePay: this.opts.googleBtnText || 'donate',
				paypal: 'paypal'
			},
			layout: {
				maxColumns: 1,
				overflow: 'never',
			},
			paymentMethods: {
				applePay: this.getApplePaySetting(),
				...this.getPaymentMethodExclusions(paymentMethods)
			},
			paymentMethodOrder: paymentMethods
		}

		this.expressCheckout = this.expressElements.create('expressCheckout', options);
		this.mountedPaymentMethods = paymentMethods;

		this.bindEvents();
		this.mountCheckout();
	}

	recreateCheckout(paymentMethods) {
		console.log("StripeExpressElements.recreateCheckout", paymentMethods);
		this.expressCheckout.destroy();
		this.mounted = false;
		this.createCheckout(paymentMethods)
	}

	bindEvents() {
		this.expressCheckout.on('ready', this.onReady.bind(this));
		this.expressCheckout.on('loaderror', this.onLoadError.bind(this));
		this.expressCheckout.on('click', this.onClick.bind(this));
		this.expressCheckout.on('confirm', this.onConfirm.bind(this));
		this.expressCheckout.on('cancel', this.onCancel.bind(this));
		this.expressCheckout.on('error', this.onError.bind(this));
	}
	
	mountCheckout() {
		console.log("StripeExpressElements.mountCheckout");
		this.expressCheckout.mount(this.container[0]);
	}

	onReady(e) {
		console.log("StripeExpressElements.onReady", e);

		if(e.availablePaymentMethods) {
			this.mounted = true;
			this.parent.updateDonationTotal();
		}
	}

	onLoadError(e) {
		console.log("StripeExpressElements.onLoadError", e);
	}

	// https://docs.stripe.com/js/elements_object/express_checkout_element_click_event
	onClick(e) {
		console.log("StripeExpressElements.onClick", e);

		AnalyticsEvent.heap('DonationExpressElementClicked', {
			paymentMethod: e.expressPaymentType
		});

		this.setPaymentMethod();

		// This triggers the showing of the payment interface (PayPal, Google/Apple Pay)
		e.resolve();

		this.toggleSpinner(true);
	}

	setPaymentMethod() {
		this.parent.usedExpressCheckoutButton = true;
		this.isSelectedMethod = true;
	}

	onConfirm(e) {
		console.log("StripeExpressElements.onConfirm", e);
		this.preparePayment(e);
	}

	onCancel(e) {
		console.log("StripeExpressElements.onCancel", e);
		this.parent.usedExpressCheckoutButton = false;
		this.isSelectedMethod = false;
		this.toggleSpinner(false);
	}

	onError(e) {
		console.log("StripeExpressElements.onError", e);
	}

	// This is used for creating a PaymentIntent (done on server by calling Stripe API)
	// It returns a client secret which is used for completing an Express Checkout Elements payment
	preparePayment(e) {
		console.log("StripeExpressElements.preparePayment", e);

		this.parent.ui.paymentMethodField.val("express_checkout--" + e.expressPaymentType);

		fetch(this.parent.ui.forms.donation.data('prepareEndpoint'), {
			method: 'POST',
			body: new FormData(this.parent.ui.forms.donation[0]),
		})
		.then(response => response.json())
		.then(response => {
			console.log("StripeExpressElements.preparePayment.response", response);

			if(!response.success) {
				console.log("StripeExpressElements.preparePayment.failed", response);
				this.handleErrors(response);
				return;
			}

			this.onPaymentIntentCreated(response);
		})
		.catch(error => {
			console.log("StripeExpressElements.preparePayment.error", error);
			this.handleErrors(error)
		});
	}

	onPaymentIntentCreated(response) {
		console.log("StripeExpressElements.onPaymentIntentCreated", response.paymentIntentClientSecret);

		this.parent.ui.paymentIntentIdField.val(response.paymentIntentId); // A unique identifier for this transaction.

		this.parent.stripe.confirmPayment({
			elements: this.expressElements,
			clientSecret: response.paymentIntentClientSecret,
			redirect: "if_required",
			confirmParams: {
				return_url: window.location.href,
			},
		}).then(response => {
			console.log("StripeExpressElements.onPaymentIntentCreated.response", response);

			if(response.error) {
				console.log("StripeExpressElements.onPaymentIntentCreated.error", response.error);
				this.handleErrors(response);
				return;
			}

			this.parent.ui.paymentMethodIdField.val(response.paymentIntent.payment_method);

			try {
				this.setPaymentMethod();
				this.parent.submitPayment();
			} catch (error) {
				console.log("StripeExpressElements.onPaymentIntentCreated.submitPayment.error", response.error);
				this.handleErrors(error);
			}
		})
		.catch(error => {
			console.log("StripeExpressElements.onPaymentIntentCreated.error", error);
			this.handleErrors(error);
		});
	}

	updateAmount(amountInPence) {
		if(amountInPence > 0 && this.expressElements) {
			this.expressElements.update({
				amount: amountInPence,
			});
		}
	}

	handleErrors(response) {
		this.parent.showDeclinedScreen(response);
		this.expressCheckout.destroy();
		this.mounted = false;
	}

	toggleSpinner(showSpinner) {
		console.log("StripeExpressElements.toggleSpinner");
		this.parent.ui.paymentMethodStep.find("> :first-child").collapse(showSpinner ? 'hide' : 'show');
		this.parent.ui.paymentMethodSpinner.collapse(showSpinner ? 'show' : 'hide');
	}

	checkMountedPaymentMethods(amount) {
		console.log("StripeExpressElements.checkMountedPaymentMethods", amount);

		if(amount >= LOW_VALUE_THRESHOLD && this.mountedPaymentMethods != ALL_PAYMENT_METHODS) {
			this.recreateCheckout(ALL_PAYMENT_METHODS);
		} else if(amount < LOW_VALUE_THRESHOLD && this.mountedPaymentMethods != LOW_VALUE_PAYMENT_METHODS) {
			this.recreateCheckout(LOW_VALUE_PAYMENT_METHODS);
		}
	}

	// Used to disable particular payment methods. Example response: `{ paypal: 'never' }`
	getPaymentMethodExclusions(paymentMethodsToShow) {
		return ALL_PAYMENT_METHODS.filter(paymentMethod => !paymentMethodsToShow.includes(paymentMethod))
			.reduce((obj, paymentMethod) => {
				obj[paymentMethod] = 'never';
				return obj;
			}, {});
	}

	// 'auto' - this covers Safari (where it will be enabled if Apple Pay is set up), as well as non-supported browsers (the button won't show)
	// 'always' - this covers desktop Chromium, which will show a QR code you can scan with your iPhone
	getApplePaySetting() {
		const isChromium = !!window.chrome;
		const isDesktop = navigator.userAgentData?.mobile === false;
		return isChromium && isDesktop ? 'always' : 'auto';
	}
}